import type { MilestoneReminderValues } from './components/ReminderPills/ReminderPills';
import type { DeadlineMilestoneIdValue } from './milestones';
import type { RemindersById } from './useHydrateDeadlineValues';
import type { DeadlineMilestoneValue, DeadlineMilestoneValues } from './validation';
import type { FrequencyValue } from '@/constants/Frequency';

const DeadlineWriteTarget = {
    MILESTONE: 'milestone',
    RECURRENCE: 'recurrence',
} as const;

interface MilestoneWriteAttempt {
    target: typeof DeadlineWriteTarget.MILESTONE;
    id: DeadlineMilestoneIdValue;
    storedValue: DeadlineMilestoneValue;
    /** Launch milestones only: an orphan milestone carries no reminders of its own. */
    storedReminders?: MilestoneReminderValues;
}

interface RecurrenceWriteAttempt {
    target: typeof DeadlineWriteTarget.RECURRENCE;
    storedFrequency?: FrequencyValue;
}

type DeadlineWriteAttempt = MilestoneWriteAttempt | RecurrenceWriteAttempt;

/**
 * A cycle that carries no recurrence stores `undefined` legitimately, so the flag — never the
 * value — is what says the recurrence belongs to this rollback.
 */
interface DeadlinesRollback {
    values: DeadlineMilestoneValues;
    remindersById: RemindersById;
    frequency?: FrequencyValue;
    hasRefusedRecurrence: boolean;
}

interface BuildDeadlinesRollbackParams {
    attempts: readonly DeadlineWriteAttempt[];
    results: readonly PromiseSettledResult<unknown>[];
}

/**
 * `attempts[i]` and `results[i]` must describe the SAME request: nothing in the types enforces that
 * pairing, and a mismatched pair puts back a milestone the server accepted.
 */
const buildDeadlinesRollback = ({
    attempts,
    results,
}: BuildDeadlinesRollbackParams): DeadlinesRollback => {
    const rollback: DeadlinesRollback = {
        values: {},
        remindersById: {},
        hasRefusedRecurrence: false,
    };

    const refusedAttempts = attempts.filter(
        (_attempt, index) => results[index]?.status === 'rejected',
    );

    for (const attempt of refusedAttempts) {
        if (attempt.target === DeadlineWriteTarget.RECURRENCE) {
            rollback.hasRefusedRecurrence = true;
            rollback.frequency = attempt.storedFrequency;

            continue;
        }

        rollback.values[attempt.id] = attempt.storedValue;

        if (attempt.storedReminders) {
            rollback.remindersById[attempt.id] = attempt.storedReminders;
        }
    }

    return rollback;
};

export { buildDeadlinesRollback, DeadlineWriteTarget };
export type { DeadlinesRollback, DeadlineWriteAttempt };
